import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Paper,
  Divider,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';

const parseTimeSlot = (slotStr) => {
  if (!slotStr) return { hour: 7, minute: 0, period: 'AM', duration: 60 };
  const match = slotStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (match) {
    let hour = parseInt(match[1], 10);
    let minute = parseInt(match[2], 10);
    let period = match[3].toUpperCase();
    if (hour < 1 || hour > 12) hour = 7;
    return { hour, minute, period, duration: 60 };
  }
  return { hour: 7, minute: 0, period: 'AM', duration: 60 };
};

const formatTimeSlot = (hour, minute, period, durationMinutes = 60) => {
  const h24 = (hour % 12) + (period === 'PM' ? 12 : 0);
  const totalStartMins = h24 * 60 + minute;
  const totalEndMins = (totalStartMins + durationMinutes) % (24 * 60);

  const formatOne = (totalMins) => {
    let h = Math.floor(totalMins / 60);
    let m = totalMins % 60;
    let p = h >= 12 ? 'PM' : 'AM';
    let h12 = h % 12;
    if (h12 === 0) h12 = 12;
    return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${p}`;
  };

  return `${formatOne(totalStartMins)} – ${formatOne(totalEndMins)}`;
};

export default function ClockTimeSlotPicker({
  open,
  initialValue,
  onClose,
  onSelect,
  title = 'Select Time Slot',
}) {
  const [selectedHour, setSelectedHour] = useState(7);
  const [selectedMinute, setSelectedMinute] = useState(0);
  const [period, setPeriod] = useState('AM');
  const [mode, setMode] = useState('hour'); // 'hour' | 'minute'
  const duration = 60; // Standard 1-hour session

  useEffect(() => {
    if (open) {
      const parsed = parseTimeSlot(initialValue);
      setSelectedHour(parsed.hour);
      setSelectedMinute(parsed.minute);
      setPeriod(parsed.period);
      setMode('hour');
    }
  }, [open, initialValue]);

  const currentSlot = formatTimeSlot(selectedHour, selectedMinute, period, duration);

  // Clock geometry: 220px diameter, center at (110, 110), radius = 80px
  const cx = 110;
  const cy = 110;
  const radius = 80;

  const hours = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  const activeAngleDeg =
    mode === 'hour'
      ? (selectedHour % 12) * 30 - 90
      : (selectedMinute / 5) * 30 - 90;

  const activeAngleRad = (activeAngleDeg * Math.PI) / 180;
  const handEndX = cx + radius * Math.cos(activeAngleRad);
  const handEndY = cy + radius * Math.sin(activeAngleRad);

  const handleApply = () => {
    onSelect(currentSlot);
    onClose();
  };

  const handlePresetClick = (h, m, p) => {
    setSelectedHour(h);
    setSelectedMinute(m);
    setPeriod(p);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          pt: 2,
          px: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTimeIcon sx={{ color: '#0284c7' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
            {title}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, py: 1.5 }}>
        {/* Digital Time Header Banner */}
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 2,
            bgcolor: '#0f172a',
            color: '#ffffff',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {/* Time digits */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Box
              component="button"
              type="button"
              onClick={() => setMode('hour')}
              sx={{
                bgcolor: mode === 'hour' ? '#0284c7' : 'rgba(255,255,255,0.1)',
                color: '#ffffff',
                border: 'none',
                borderRadius: 1.5,
                px: 1.2,
                py: 0.5,
                fontSize: '1.8rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {String(selectedHour).padStart(2, '0')}
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#94a3b8' }}>
              :
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={() => setMode('minute')}
              sx={{
                bgcolor: mode === 'minute' ? '#0284c7' : 'rgba(255,255,255,0.1)',
                color: '#ffffff',
                border: 'none',
                borderRadius: 1.5,
                px: 1.2,
                py: 0.5,
                fontSize: '1.8rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {String(selectedMinute).padStart(2, '0')}
            </Box>
          </Box>

          {/* AM / PM Toggles */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box
              component="button"
              type="button"
              onClick={() => setPeriod('AM')}
              sx={{
                bgcolor: period === 'AM' ? '#38bdf8' : 'rgba(255,255,255,0.1)',
                color: period === 'AM' ? '#0f172a' : '#94a3b8',
                border: 'none',
                borderRadius: 1,
                px: 1.2,
                py: 0.3,
                fontSize: '0.75rem',
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              AM
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => setPeriod('PM')}
              sx={{
                bgcolor: period === 'PM' ? '#38bdf8' : 'rgba(255,255,255,0.1)',
                color: period === 'PM' ? '#0f172a' : '#94a3b8',
                border: 'none',
                borderRadius: 1,
                px: 1.2,
                py: 0.3,
                fontSize: '0.75rem',
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              PM
            </Box>
          </Box>
        </Paper>

        {/* Circular Clock Dial */}
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
          <Box
            sx={{
              position: 'relative',
              width: 220,
              height: 220,
              borderRadius: '50%',
              bgcolor: '#f8fafc',
              border: '2px solid #e2e8f0',
              boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.04)',
              userSelect: 'none',
            }}
          >
            {/* SVG Hand & Center Pivot */}
            <svg
              width="220"
              height="220"
              style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
            >
              <line
                x1={cx}
                y1={cy}
                x2={handEndX}
                y2={handEndY}
                stroke="#0284c7"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <circle cx={cx} cy={cy} r="5" fill="#0284c7" />
            </svg>

            {/* Dial Numbers */}
            {(mode === 'hour' ? hours : minutes).map((val) => {
              const deg = mode === 'hour' ? (val % 12) * 30 - 90 : (val / 5) * 30 - 90;
              const rad = (deg * Math.PI) / 180;
              const x = cx + radius * Math.cos(rad);
              const y = cy + radius * Math.sin(rad);
              const isSelected = mode === 'hour' ? selectedHour === val : selectedMinute === val;

              return (
                <Box
                  key={val}
                  onClick={() => {
                    if (mode === 'hour') {
                      setSelectedHour(val);
                      setMode('minute');
                    } else {
                      setSelectedMinute(val);
                    }
                  }}
                  sx={{
                    position: 'absolute',
                    left: x,
                    top: y,
                    transform: 'translate(-50%, -50%)',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: isSelected ? 800 : 500,
                    bgcolor: isSelected ? '#0284c7' : 'transparent',
                    color: isSelected ? '#ffffff' : '#334155',
                    boxShadow: isSelected ? '0 2px 6px rgba(2, 132, 199, 0.4)' : 'none',
                    transition: 'all 0.15s',
                    '&:hover': {
                      bgcolor: isSelected ? '#0284c7' : '#e0f2fe',
                      color: isSelected ? '#ffffff' : '#0369a1',
                    },
                  }}
                >
                  {mode === 'hour' ? val : String(val).padStart(2, '0')}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Quick Minutes Selection */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mt: 1,
            mb: 1.5,
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            Minutes:
          </Typography>
          {[0, 15, 30, 45].map((m) => (
            <Chip
              key={m}
              label={`:${String(m).padStart(2, '0')}`}
              size="small"
              clickable
              color={selectedMinute === m ? 'primary' : 'default'}
              variant={selectedMinute === m ? 'filled' : 'outlined'}
              onClick={() => setSelectedMinute(m)}
              sx={{ fontWeight: 600, height: 24, fontSize: '0.75rem' }}
            />
          ))}
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Common Session Slots */}
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.5 }}
          >
            Common Session Slots:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
            {[
              { label: '6 AM', h: 6, m: 0, p: 'AM' },
              { label: '7 AM', h: 7, m: 0, p: 'AM' },
              { label: '8 AM', h: 8, m: 0, p: 'AM' },
              { label: '9 AM', h: 9, m: 0, p: 'AM' },
              { label: '10 AM', h: 10, m: 0, p: 'AM' },
              { label: '4 PM', h: 4, m: 0, p: 'PM' },
              { label: '5 PM', h: 5, m: 0, p: 'PM' },
              { label: '6 PM', h: 6, m: 0, p: 'PM' },
              { label: '7 PM', h: 7, m: 0, p: 'PM' },
              { label: '8 PM', h: 8, m: 0, p: 'PM' },
            ].map((slot) => {
              const isMatch =
                selectedHour === slot.h && selectedMinute === slot.m && period === slot.p;
              return (
                <Chip
                  key={slot.label}
                  label={slot.label}
                  size="small"
                  clickable
                  color={isMatch ? 'primary' : 'default'}
                  variant={isMatch ? 'filled' : 'outlined'}
                  onClick={() => handlePresetClick(slot.h, slot.m, slot.p)}
                  sx={{ fontSize: '0.75rem', height: 24 }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Result Preview Box */}
        <Paper
          variant="outlined"
          sx={{
            p: 1,
            mt: 1.5,
            bgcolor: '#f0fdf4',
            borderColor: '#86efac',
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="caption" sx={{ color: '#166534', fontWeight: 600 }}>
            Resulting Slot:
          </Typography>
          <Typography variant="subtitle2" sx={{ color: '#15803d', fontWeight: 800 }}>
            {currentSlot}
          </Typography>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose} size="small" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          size="small"
          startIcon={<CheckIcon />}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Set Time Slot
        </Button>
      </DialogActions>
    </Dialog>
  );
}
